import { useState } from 'react'
import {
  Divider,
  IconButton,
  IconButtonProps,
  Menu,
  MenuItem,
  SvgIcon,
  Typography
} from '@mui/material'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { useAppStore } from '@/contexts/store'
import { isHappa } from '@/hooks'
import { isActive } from '@/utils/path'

const MenuSwitcher = (props: IconButtonProps) => {
  const { menu } = useAppStore()
  const router = useRouter()
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const menuOpen = Boolean(menuAnchorEl)

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget)
  }
  const handleMenuClose = () => {
    setMenuAnchorEl(null)
  }

  return (
    <>
      <IconButton
        size="medium"
        onClick={handleMenuClick}
        color="secondary"
        {...props}
        sx={{ backgroundColor: isHappa ? 'transparent' : undefined }}
      >
        <SvgIcon fontSize="medium">
          <use href={menuOpen ? '#ico-close' : '#ico-ham-menu'} />
        </SvgIcon>
      </IconButton>

      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: '1.6rem' }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              minWidth: '21.2rem',
              backgroundColor: theme.palette.text.primaryContrastText,
              borderRadius: theme.shape.borderRadius4,
              boxShadow: theme.shadows[1],
              padding: `${theme.spacing(3)} 0`,
              '& ul': {
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '1.6rem'
              }
            })
          }
        }}
      >
        {menu?.main_menu_02?.menu.map((item) => (
          <MenuItem
            key={item.url}
            component={Link}
            href={item.url}
            scroll={false}
            onClick={handleMenuClose}
            selected={isActive(item.url, router.pathname)}
            sx={(theme) => ({
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '0.8rem',
              color: theme.palette.text.secondary,
              padding: `0 ${theme.spacing(3)}`,
              height: '2.4rem',
              transition: `all 0.3s ${theme.customEasings.outCubic}`,
              '&.Mui-selected': {
                color: theme.palette.primary.main,
                backgroundColor: 'transparent',
                pointerEvents: 'none',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: '0.4rem',
                  height: '2.4rem',
                  backgroundColor: theme.palette.primary.main,
                  borderTopRightRadius: theme.shape.borderRadius360,
                  borderBottomRightRadius: theme.shape.borderRadius360
                }
              },
              '&:hover': {
                color: theme.palette.primary.main,
                backgroundColor: 'transparent'
              }
            })}
            disableRipple
            disableTouchRipple
          >
            <Typography variant={'h6'} component="span" fontWeight={500}>
              {item.title}
            </Typography>
          </MenuItem>
        ))}
        <Divider sx={{ margin: (theme) => `0 ${theme.spacing(3)} !important` }} />
        {menu?.main_menu_03?.menu.map((item) => (
          <MenuItem
            key={item.url}
            component={Link}
            href={item.url}
            scroll={false}
            onClick={handleMenuClose}
            selected={isActive(item.url, router.pathname)}
            sx={(theme) => ({
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.8rem',
              color: theme.palette.text.secondary,
              padding: `0 ${theme.spacing(3)}`,
              height: '2.4rem',
              transition: `all 0.3s ${theme.customEasings.outCubic}`,
              '&.Mui-selected': {
                color: theme.palette.primary.main,
                backgroundColor: 'transparent',
                pointerEvents: 'none'
              },
              '&:hover': {
                color: theme.palette.primary.main,
                backgroundColor: 'transparent'
              }
            })}
            disableRipple
            disableTouchRipple
          >
            <Typography variant="menu" component="span" fontWeight={500}>
              {item.title}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default MenuSwitcher
